import { MagpieConnection } from 'magpie-lib/connection';
import { AudienceFilter, defineMessage, defineTopic } from 'magpie-lib/models';
import * as t from 'ts-schema';
/**
 * Same-window Magpie channel for the Academy Assistant. Everything published
 * here is reachable from any React root on the page — including a separately
 * bundled one — without a shared React context or the page-global
 * `breezeEngineRegistry` (which would collide with Breeze Assistant).
 *
 * Layout (open/close/toggleMode) is driven directly through the persisted
 * `useAssistantLayout` store, so the channel carries: the host-handled actions
 * with no backing store (`ask`, `openConversation`, `startNew`, `focusInput`)
 * and a `contextChanged` broadcast — the resolved video-context snapshot
 * mirrored out of `academyAssistantContextStore` so a consumer in another
 * module instance still reaches the host's store. Ownership/auto-clear stay
 * consumer-side; only the resolved snapshot (never the non-serializable owner
 * Symbol) crosses here.
 */
const TOPIC_NAME = '@@academy-ai-assistant/channel';

/**
 * Per-turn options for an `ask`. All optional; omitting them reproduces the
 * original `ask(text)` behavior.
 */

const askMessage = defineMessage({
  type: '@@academy-ai-assistant/ask',
  // `mode`/`source` are optional per-turn options.
  payloadSchema: t.object({
    text: t.string,
    mode: t.optional(t.string),
    source: t.optional(t.string)
  })
});
const openConversationMessage = defineMessage({
  type: '@@academy-ai-assistant/open-conversation',
  payloadSchema: t.object({
    conversationId: t.number
  })
});
const startNewMessage = defineMessage({
  type: '@@academy-ai-assistant/start-new',
  payloadSchema: t.object({})
});
const focusInputMessage = defineMessage({
  type: '@@academy-ai-assistant/focus-input',
  payloadSchema: t.object({})
});
const openChatListMessage = defineMessage({
  type: '@@academy-ai-assistant/open-chat-list',
  payloadSchema: t.object({
    mode: t.optional(t.string)
  })
});
const contextChangedMessage = defineMessage({
  type: '@@academy-ai-assistant/context-changed',
  payloadSchema: t.object({
    catalogId: t.optional(t.string),
    videoTimestampSeconds: t.optional(t.number),
    lessonId: t.optional(t.string),
    trackId: t.optional(t.string)
  })
});
const academyAssistantTopic = defineTopic({
  name: TOPIC_NAME,
  messages: [askMessage, openConversationMessage, startNewMessage, focusInputMessage, openChatListMessage, contextChangedMessage],
  // Types are stable within our own bundle; skip the per-message runtime
  // validation cost (and its mandatory `onValidationError` on every publish).
  enableRuntimeValidation: false,
  // Same window only — no cross-tab/cross-portal fan-out. Do NOT `excludeSelf`:
  // publisher and host subscriber can share a client, and we still want delivery.
  audience: AudienceFilter.builder().sameWindow().build()
});
const topic = () => MagpieConnection.get().forTopic(academyAssistantTopic);

/**
 * Narrow an untrusted wire `mode` to a real `AssistantOpenMode` (runtime
 * validation is off) so a stray value can't reach `setMode`.
 */
const toOpenMode = mode => mode === 'sidebar' || mode === 'fullscreen' ? mode : undefined;

/** Ask the assistant a question (opening/seeding a turn is the host's job). */
export const publishAsk = (text, options) => {
  // Send every key explicitly — the schema treats optionals as present-but-null.
  topic().publish(askMessage.create({
    text,
    mode: options === null || options === void 0 ? void 0 : options.mode,
    source: options === null || options === void 0 ? void 0 : options.source
  }));
};

/** Ask the host to open the chat/list view (fresh chat, history visible). */
export const publishOpenChatList = options => {
  topic().publish(openChatListMessage.create({
    mode: options === null || options === void 0 ? void 0 : options.mode
  }));
};

/** Ask the host to switch to an existing conversation. */
export const publishOpenConversation = conversationId => {
  topic().publish(openConversationMessage.create({
    conversationId
  }));
};

/** Ask the host to start a fresh conversation. */
export const publishStartNew = () => {
  topic().publish(startNewMessage.create({}));
};

/** Subscribe to `ask` intents. Returns an unsubscribe fn. */
export const onAsk = listener => topic().on(askMessage.getType(), message => {
  // Coerce the present-but-nullable schema keys back to real optionals.
  const {
    text,
    mode,
    source
  } = message.payload;
  listener(text, {
    mode: toOpenMode(mode),
    source: source !== null && source !== void 0 ? source : undefined
  });
});

/** Subscribe to `openChatList` intents. Returns an unsubscribe fn. */
export const onOpenChatList = listener => topic().on(openChatListMessage.getType(), message => {
  listener({
    mode: toOpenMode(message.payload.mode)
  });
});

/** Subscribe to `openConversation` intents. Returns an unsubscribe fn. */
export const onOpenConversation = listener => topic().on(openConversationMessage.getType(), message => {
  listener(message.payload.conversationId);
});

/** Subscribe to `startNew` intents. Returns an unsubscribe fn. */
export const onStartNew = listener => topic().on(startNewMessage.getType(), () => {
  listener();
});

/** Ask the host to focus the chat text input, if one is currently mounted. */
export const publishFocusInput = () => {
  topic().publish(focusInputMessage.create({}));
};

/** Subscribe to `focusInput` intents. Returns an unsubscribe fn. */
export const onFocusInput = listener => topic().on(focusInputMessage.getType(), () => {
  listener();
});

/** Broadcast the resolved video-context snapshot to the host's store. */
export const publishContextChanged = context => {
  // Send every key explicitly — the schema treats them as present-but-nullable.
  topic().publish(contextChangedMessage.create({
    catalogId: context.catalogId,
    videoTimestampSeconds: context.videoTimestampSeconds,
    lessonId: context.lessonId,
    trackId: context.trackId
  }));
};

/** Subscribe to context-snapshot broadcasts. Returns an unsubscribe fn. */
export const onContextChanged = listener => topic().on(contextChangedMessage.getType(), message => {
  const {
    catalogId,
    videoTimestampSeconds,
    lessonId,
    trackId
  } = message.payload;
  listener({
    catalogId: catalogId !== null && catalogId !== void 0 ? catalogId : undefined,
    videoTimestampSeconds: videoTimestampSeconds !== null && videoTimestampSeconds !== void 0 ? videoTimestampSeconds : undefined,
    lessonId: lessonId !== null && lessonId !== void 0 ? lessonId : undefined,
    trackId: trackId !== null && trackId !== void 0 ? trackId : undefined
  });
});